import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';

import { getCurvePaletteColor } from '../../shared/curvePalette';
import { degreesToRadians } from '../angleUtils';
import { MARGIN_TOP } from '../consts';
import styles from '../DirectionalDrillingMonitor.module.css';
import type { ToolState } from '../store';

interface PointsProps {
  theme: 'dark' | 'light';
  width: number;
  height: number;
  pixelRatio: number;
  toolState: ToolState;
}

export const ToolRender = observer(function ToolRender({ toolState, theme, width, height, pixelRatio }: PointsProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    canvas.width = width * pixelRatio;
    canvas.height = height * pixelRatio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const s = Math.min(width, height);
    const r = s / 2 - MARGIN_TOP;

    const cx = width / 2;
    const cy = height / 2;

    const ctx = canvas.getContext('2d');
    if (ctx && r > 0) {
      ctx.scale(pixelRatio, pixelRatio);
      ctx.clearRect(0, 0, width, height);
      if (toolState.isVisible) {
        toolDrawer(ctx, toolState, r, theme, cx, cy);
      }
    }
  }, [
    theme,
    pixelRatio,
    width,
    height,
    toolState,
    toolState.points,
    toolState.color,
    toolState.isVisible,
    toolState.pointsQuantity,
  ]);

  return (
    <canvas
      ref={canvasRef}
      width={width * pixelRatio}
      height={height * pixelRatio}
      className={styles.canvas}
      aria-hidden="true"
      style={{ width, height }}
    />
  );
});

function toolDrawer(
  ctx: CanvasRenderingContext2D,
  tool: ToolState,
  chartR: number,
  theme: 'dark' | 'light',
  cx: number,
  cy: number,
) {
  const { points, pointsQuantity: amount, color } = tool;

  ctx.fillStyle = getCurvePaletteColor(theme, color);

  ctx.strokeStyle = getCurvePaletteColor(theme, color);
  const pointR = Math.round(chartR / amount / 2);
  const minPointR = pointR * 0.5;
  for (let i = 0; i < points.length; i++) {
    const rad = degreesToRadians(points[i]);

    const k = Math.round(chartR - (chartR * (points.length - 1 - i)) / amount) / chartR;

    let rp = pointR * k;
    if (rp < minPointR) {
      rp = minPointR;
    }

    const pointRadiusOffset = Math.round((chartR / points.length) * (i + 1) - rp);
    const x = cx + Math.sin(rad) * pointRadiusOffset;
    const y = cy - Math.cos(rad) * pointRadiusOffset;
    ctx.beginPath();
    ctx.lineWidth = 2;

    if (i === points.length - 1) {
      ctx.moveTo(cx, cy);
      ctx.lineTo(x, y);
      ctx.stroke();
    }

    if (i === points.length - 2) {
      ctx.setLineDash([5, 15]);
      ctx.moveTo(cx, cy);
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.lineWidth = 1;

    ctx.moveTo(x, y);
    ctx.arc(x, y, rp, 0, 2 * Math.PI);
    ctx.fill();
    ctx.closePath();
  }
}
